import { IconChevronDown } from '@tabler/icons-react';
import { Box, Card, Divider, Flex, Group, Paper, Skeleton, Stack, Text } from '@mantine/core';
import { ReactElement } from 'react';

export function TimelineHeaderSkeleton(): ReactElement {
  return (
    <Flex
      justify="space-between"
      align="center"
      px="md"
      py="sm"
      style={{ border: '1px solid var(--mantine-color-gray-3)' }}
      w="100%"
    >
      <Flex gap="md" align="center">
        <Skeleton radius="lg" h="53" w="53" />
        <Stack gap={'xs'}>
          <Skeleton h={23} w={200} />
          <Skeleton h={18} w={100} />
        </Stack>
      </Flex>

      <Group gap="xs">
        <Skeleton height={42} width={48} radius="xl" />
        <Skeleton height={42} width={48} radius="xl" />
        <Skeleton height={42} width={48} radius="xl" />
        <Skeleton height={42} width={48} radius="xl" />
      </Group>
    </Flex>
  );
}

export function PatientSummarySkeleton(): ReactElement {
  return (
    <Card withBorder radius={'lg'}>
      <Stack gap="xs" style={{ maxWidth: 400 }}>
        {/* Contact info header */}
        <Flex align="center" gap="xs">
          <IconChevronDown size={16} />
          <Text size="md" w={500} c="dimmed" fw={600}>
            Contact Info
          </Text>
        </Flex>

        <Divider my="md" />

        {/* Care team header */}

        <Flex align="center" gap="xs">
          <IconChevronDown size={16} />
          <Text size="md" w={500} c="dimmed" fw={600}>
            Care Team
          </Text>
        </Flex>

        <Group gap={'md'} align={'center'} justify={'space-between'} pr={'md'}>
          <Skeleton width={'60%'} height={24} radius="sm" />
          <Skeleton width={'20%'} height={24} radius="sm" />
          <Skeleton width={'60%'} height={24} radius="sm" />
          <Skeleton width={'20%'} height={24} radius="sm" />
          <Skeleton width={'60%'} height={24} radius="sm" />
          <Skeleton width={'20%'} height={24} radius="sm" />
          <Skeleton width={'60%'} height={24} radius="sm" />
          <Skeleton width={'20%'} height={24} radius="sm" />
        </Group>
      </Stack>
    </Card>
  );
}

export function PartnerSummarySkeleton(): ReactElement {
  return (
    <Card withBorder radius={'lg'}>
      <Stack gap="xs" style={{ maxWidth: 400 }}>
        {/* Contact info header */}
        <Flex align="center" gap="xs">
          <IconChevronDown size={16} />
          <Text size="md" w={500} c="dimmed" fw={600}>
            Contact Info
          </Text>
        </Flex>

        <Skeleton width={'100%'} height={60} radius="sm" />

        <Divider my="md" />

        {/* Addresses header */}
        <Flex align="center" gap="xs">
          <IconChevronDown size={16} />
          <Text size="md" w={500} c="dimmed" fw={600}>
            Addresses
          </Text>
        </Flex>

        <Skeleton width={'100%'} height={60} radius="sm" />

        <Divider my="md" />

        {/* Contacts header */}
        <Flex align="center" gap="xs">
          <IconChevronDown size={16} />
          <Text size="md" w={500} c="dimmed" fw={600}>
            Contacts
          </Text>
        </Flex>

        <Skeleton width={'100%'} height={60} radius="sm" />
        <Skeleton width={'100%'} height={60} radius="sm" />
      </Stack>
    </Card>
  );
}

export function DocumentsSkeleton(): ReactElement {
  return (
    <Card withBorder radius={'lg'}>
      <Stack>
        <Text size="md" fw="bold" c="dimmed">
          Patient Documents
        </Text>

        <Paper
          p="sm"
          withBorder
          radius="sm"
          style={{
            backgroundColor: 'var(--mantine-color-gray-0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          h={80}
        >
          <Skeleton w={'100%'} height={'80%'} />
        </Paper>
        <Paper p="sm" withBorder radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }} h={80}>
          <Skeleton w={'100%'} height={'80%'} />
        </Paper>
        <Paper p="sm" withBorder radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }} h={80}>
          <Skeleton w={'100%'} height={'80%'} />
        </Paper>
      </Stack>
    </Card>
  );
}

type TimelineMessageSkeletonProps = {
  align?: Array<'flex-start' | 'flex-end'>;
};

export function TimelineMessageSkeleton({
  align = ['flex-start', 'flex-end', 'flex-end', 'flex-end', 'flex-start'],
}: TimelineMessageSkeletonProps): ReactElement {
  return (
    <Card radius="lg" withBorder p="sm" style={{ display: 'flex', flexDirection: 'column', gap: 'sm' }}>
      {/* Header row */}
      <Flex justify="space-between" align="center">
        <Group align="center" gap="sm" justify="center">
          <Stack gap={4}>
            <Skeleton height={16} width={180} /> {/* ItemTitle */}
            <Skeleton height={12} width={100} /> {/* ItemSubtitle */}
          </Stack>
        </Group>
        <Skeleton height={20} width={60} /> {/* timestamp */}
      </Flex>

      <Divider my="md" />

      {/* Message bubbles */}
      {align.map((a, index) => (
        <Flex key={index} justify={a} my="xs">
          <Stack
            gap={4}
            style={{
              borderRadius: 12,
              padding: '8px 12px',
              backgroundColor: 'var(--mantine-color-gray-0)',
              border: '1px solid var(--mantine-color-gray-3)',
            }}
          >
            <Group gap={8} align="center">
              <Skeleton height={24} width={24} circle /> {/* avatar */}
              <Skeleton my={4} height={12} width={80} /> {/* sender */}
              <Skeleton my={4} height={10} width={40} /> {/* time */}
            </Group>
            <Skeleton my={4} height={30} width={400} /> {/* message content */}
          </Stack>
        </Flex>
      ))}
    </Card>
  );
}

export function TimelinePhoneCallSkeleton() {
  return (
    <Card radius="lg" withBorder p="sm" style={{ display: 'flex', flexDirection: 'column', gap: 'sm' }}>
      <Flex align="center" justify="space-between" mb="xs">
        {/* Left side - call info */}
        <Group gap="sm">
          <Flex direction="column" gap="xs">
            <Skeleton height={12} width={140} /> {/* call info */}
            <Skeleton height={10} width={110} /> {/* call status */}
          </Flex>
        </Group>

        {/* Right side - user and time */}
        <Group gap="sm">
          <Skeleton height={24} width={100} radius="xl" /> {/* User name */}
          <Skeleton circle height={24} width={24} /> {/* User Avatar */}
          <Skeleton height={10} width={60} /> {/* timestamp */}
        </Group>
      </Flex>

      {/* Divider */}
      <Skeleton height={1} mt="xs" />

      {/* Empty content area */}
      <Box mt="md">
        <Skeleton height={30} radius="sm" />
      </Box>
    </Card>
  );
}

export function TimelineSkeleton() {
  return (
    <Stack px="md" mb="md">
      <TimelineMessageSkeleton />
      <TimelinePhoneCallSkeleton />
      <TimelineMessageSkeleton align={['flex-end', 'flex-start', 'flex-start']} />
      <TimelinePhoneCallSkeleton />
    </Stack>
  );
}
